// File used for reusable utility functions
import { DOCUMENT_LANGUAGES, LANGUAGES } from './settings'

import { format, parseISO } from 'date-fns'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'

export const getFirstLetter = (text: string) => {
    return text.charAt(0)
}

export const capitalizeFirstLetter = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

export const encodeUrl = (url: string | number) => {
    // encodeURIComponent - escapes all chars except A–Z a–z 0–9 - _ . ! ~ * ' ( )
    // replace -> escapes next chars . ! ~ * ' ( )
    return encodeURIComponent(url.toString()).replace(/[.!~*'()]/g, (c) => `%${c.charCodeAt(0).toString(16)}`)
}

export const buildUrl = (endpoint: string, params?: { [key: string]: string | number }): string => {
    let url = endpoint

    if (params && endpoint.includes(':')) {
        Object.keys(params).forEach((key) => {
            const encodedValue = encodeUrl(params[key])
            url = url.replace(`:${key}`, encodedValue)
        })
    } else if (endpoint.includes(':')) {
        throw new Error(`Missing required parameter for endpoint: ${endpoint}`)
    }
    return url
}

export const combineURL = (...args: string[]) => {
    const combinedUrl = args
        .filter((arg) => !!arg)
        .join('/')
        .replace(/[/]+/g, '/')
        .replace(/^(.+):\//, '$1://')
        .replace(/^file:/, 'file:/')
        .replace(/\/(\?|&|#[^!])/g, '$1')
        .replace(/\?/g, '&')
        .replace('&', '?')

    return combinedUrl.startsWith('/') ? combinedUrl : `/${combinedUrl}`
}

export const formatDatetime = (datetime: string, returnFormat: 'date' | 'datetime' = 'date') => {
    if (!datetime) {
        return ''
    }

    let formatting = ''
    switch (returnFormat) {
        case 'date':
            formatting = 'dd/MM/yyyy'
            break
        case 'datetime':
            formatting = 'dd/MM/yyyy - HH:mm'
    }

    return format(parseISO(datetime), formatting)
}

export const formatCurrency = (value: number, currency = 'EUR', locale = 'nl-BE') => {
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value)
    } catch {
        return `${value} ${currency}`
    }
}

export type MultilingualTextValue = {
    en?: string | null
    nl?: string | null
    fr?: string | null
    de?: string | null
}

export const getLocalizedText = (text: MultilingualTextValue | null | undefined, language: string) => {
    if (!text) return ''

    const currentLanguage = language.split('-')[0] as keyof MultilingualTextValue

    return text[currentLanguage] || text.en || text.nl || text.fr || text.de || ''
}

export const safeNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
    return null
}

export const getCookie = (cookieName: string) => {
    return Cookies.get(cookieName)
}

export const resolveUnitsBetweenTwoDates = (date1: number, date2: number) => {
    const firstDate = dayjs(date1)
    const secondDate = dayjs(date2)

    return {
        days: firstDate.diff(secondDate, 'day'),
        hours: Math.round(firstDate.diff(secondDate, 'hour') % 24),
        minutes: Math.round(firstDate.diff(secondDate, 'minute') % 60),
        seconds: Math.round(firstDate.diff(secondDate, 'second') % 60),
    }
}

export const mergeObjectsRecursively = <T extends {}>(target: T, source: Partial<T>) => {
    const copy = { ...target }

    for (const key in source) {
        const sourceValue = source[key]

        if (Array.isArray(sourceValue)) {
            copy[key] = sourceValue
        } else if (typeof sourceValue === 'object' && sourceValue !== null) {
            copy[key] = mergeObjectsRecursively(copy[key], sourceValue)
        } else {
            copy[key] = sourceValue
        }
    }

    return copy
}

export function extractUuidFromDigexUrl(url: string): string | null {
    try {
        const parsed = new URL(url)

        // 1. Check query parameter "reference"
        const reference = parsed.searchParams.get('reference')
        if (reference && isUuid(reference)) return reference

        // 2. Otherwise, check last path segment
        const lastSegment = parsed.pathname.split('/').filter(Boolean).pop()
        if (lastSegment && isUuid(lastSegment)) return lastSegment

        return null
    } catch {
        return null // invalid URL
    }
}

// helper to validate UUID v1–v5
function isUuid(value: string): boolean {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
    return uuidRegex.test(value)
}

/**
 * Retrieves a nested value from an object using a dot-notation string.
 * @param obj - The object to traverse
 * @param path - The string path (e.g., 'address.street')
 * @returns The value at the path, or undefined if not found
 */
export function getNestedValue<T>(obj: object, path: string): T | undefined {
    const keys = path.split('.')
    let current: unknown = obj

    for (const key of keys) {
        // Check if current is a non-null object that contains the key
        if (current && typeof current === 'object' && key in current) {
            current = (current as Record<string, unknown>)[key]
        } else {
            return undefined
        }
    }

    return current as T
}

export const getDefaultDocumentLanguage = (resolvedLanguage: string): string => {
    const isAvailable = DOCUMENT_LANGUAGES.some((l) => l.backendValue === resolvedLanguage)
    return isAvailable ? resolvedLanguage : LANGUAGES.fr.backendValue
}
